import { Box, FloatingIndicator, NavLink, Stack, Text, Tooltip } from "@mantine/core";
import { useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import Icon from "src/components/common/Icon";
import { helpTourTargetAttribute } from "src/components/help/HelpTourTarget";
import type { ILayoutItemContext, ILayoutLink } from "src/contexts/layouts/LayoutContext";
import { menuTourTarget } from "src/help/helpTourTargets";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import { getLayoutLinkIcon } from "src/routing/navigationIcons";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

import { getSelectedLayoutHref } from "./NavMenu";

/**
 * Defines the inputs for this navigation presentation.
 */
interface ISubMenuProps {
    title?: string;
    menuItems: ILayoutLink[];
    context?: ILayoutItemContext;
    collapsed?: boolean;
    onNavigate?: () => void;
}

/**
 * Renders context-filtered navigation, omitting the menu when no allowed items remain.
 *
 * Postconditions: Collapsed items omit visible labels and provide tooltips. Clicks invoke the item
 * action before onNavigate. The longest path prefix selects the active item; titles stay hidden.
 */
const SubMenu = ({ title, menuItems, context, collapsed = false, onNavigate }: ISubMenuProps) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();
    const allowedItems = useMemo(
        () => filterLayoutLinksByPermission(permissions, menuItems, context),
        [context, menuItems, permissions]
    );
    const selectedHref = getSelectedLayoutHref(location.pathname, allowedItems);
    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlRefs, setControlRefs] = useState<Record<string, HTMLElement | null>>({});
    const controlRefCallbacks = useRef<Record<string, (node: HTMLElement | null) => void>>({});

    /**
     * Reuses a callback per href and records non-null mounted elements for the indicator.
     *
     * Postconditions: Repeated registration of the same node keeps state unchanged; null callbacks
     * are ignored and do not remove stored elements.
     */
    const getControlRef = (href: string) => {
        const existingCallback = controlRefCallbacks.current[href];

        if (existingCallback) {
            return existingCallback;
        }

        /**
         * Requests the current element mapping only when a non-null node changes.
         */
        const callback = (node: HTMLElement | null) => {
            if (!node) {
                return;
            }

            setControlRefs(current =>
                current[href] === node ? current : { ...current, [href]: node }
            );
        };

        controlRefCallbacks.current[href] = callback;
        return callback;
    };

    if (allowedItems.length === 0) {
        return null;
    }

    return (
        <Box>
            {/* Modern layout does not show submenu headers in the submenus. */}
            {/* {title && !collapsed && (
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="md" py="xs" truncate>
                    {title}
                </Text>
            )} */}
            <Stack
                ref={setRootRef}
                gap={0}
                style={{ position: "relative" }}
            >
                <FloatingIndicator
                    target={selectedHref ? controlRefs[selectedHref] : null}
                    parent={rootRef}
                    transitionDuration={200}
                    style={{
                        backgroundColor: "var(--mantine-primary-color-light)",
                        pointerEvents: "none"
                    }}
                />
                {allowedItems.map(item => {
                    const active = selectedHref === item.href;
                    const link = (
                        <NavLink
                            key={item.href}
                            ref={getControlRef(item.href)}
                            component={item.actionFn ? "button" : RouterLink}
                            to={item.actionFn ? undefined : item.href}
                            target={item.newTab ? "_blank" : undefined}
                            onClick={() => {
                                item.actionFn?.();
                                onNavigate?.();
                            }}
                            active={active}
                            label={collapsed ? undefined : item.label}
                            aria-label={item.label}
                            {...{ [helpTourTargetAttribute]: menuTourTarget(item.label) }}
                            leftSection={
                                <Icon
                                    icon={getLayoutLinkIcon(item)}
                                    fixedWidth
                                />
                            }
                            styles={{
                                root: {
                                    borderRadius: "var(--mantine-radius-sm)",
                                    height: 40,
                                    position: "relative",
                                    zIndex: 1,
                                    backgroundColor: active ? "transparent" : undefined
                                },
                                section: collapsed ? { marginInline: "auto" } : undefined
                            }}
                            bdrs={0}
                        />
                    );

                    return collapsed ? (
                        <Tooltip
                            key={item.href}
                            label={item.label}
                            position="right"
                            withArrow
                        >
                            {link}
                        </Tooltip>
                    ) : (
                        link
                    );
                })}
            </Stack>
        </Box>
    );
};

export default SubMenu;
